import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, IconButton, Button, Autocomplete, Typography,
  InputAdornment, Select, MenuItem, Tooltip
} from '@mui/material';
import { Add, Delete, Search } from '@mui/icons-material';
import { API } from '../context/AuthContext';
import { fmt } from '../utils';

const TAX_OPTIONS = [0, 8, 16];

const DEFAULT_ITEM = { description: '', code: '', qty: 1, unitPrice: 0, discount: 0, taxRate: 16 };

function calcItem(item) {
  const base = item.qty * item.unitPrice;
  const disc = base * (item.discount / 100);
  const taxable = base - disc;
  const tax = taxable * (item.taxRate / 100);
  return { subtotal: taxable, taxAmount: tax, total: taxable + tax };
}

export default function LineItemsEditor({ items, onChange }) {
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');

  useEffect(() => {
    API.get('/products', { params: { search: productSearch, limit: 20 } })
      .then(r => setProducts(r.data.products));
  }, [productSearch]);

  const updateItem = (idx, field, val) => {
    const updated = items.map((item, i) => i === idx ? { ...item, [field]: val } : item);
    onChange(updated);
  };

  const addItem = () => onChange([...items, { ...DEFAULT_ITEM }]);

  const removeItem = (idx) => onChange(items.filter((_, i) => i !== idx));

  const selectProduct = (idx, product) => {
    if (!product) return;
    const updated = items.map((item, i) => i === idx ? {
      ...item,
      product: product._id,
      code: product.code || '',
      description: product.name,
      unitPrice: product.price,
      taxRate: product.taxRate ?? 16
    } : item);
    onChange(updated);
  };

  const totals = items.reduce((acc, item) => {
    const c = calcItem(item);
    return { subtotal: acc.subtotal + c.subtotal, tax: acc.tax + c.taxAmount, total: acc.total + c.total };
  }, { subtotal: 0, tax: 0, total: 0 });

  return (
    <Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell width={60}>Cant.</TableCell>
            <TableCell>Descripción</TableCell>
            <TableCell width={120} align="right">Precio unit.</TableCell>
            <TableCell width={80} align="right">Desc.%</TableCell>
            <TableCell width={90}>IVA</TableCell>
            <TableCell width={110} align="right">Total</TableCell>
            <TableCell width={40}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item, idx) => {
            const c = calcItem(item);
            return (
              <TableRow key={idx}>
                <TableCell>
                  <TextField size="small" type="number" value={item.qty}
                    onChange={e => updateItem(idx, 'qty', parseFloat(e.target.value) || 1)}
                    inputProps={{ min: 0.001, step: 1, style: { textAlign: 'center' } }}
                    sx={{ '& .MuiInputBase-input': { px: 1 } }}
                  />
                </TableCell>
                <TableCell>
                  <Autocomplete
                    freeSolo size="small"
                    options={products}
                    getOptionLabel={o => typeof o === 'string' ? o : o.name}
                    inputValue={item.description}
                    onInputChange={(_, val) => { updateItem(idx, 'description', val); setProductSearch(val); }}
                    onChange={(_, val) => { if (val && typeof val === 'object') selectProduct(idx, val); }}
                    renderOption={(props, o) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{o.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{o.code} · {fmt.currency(o.price)}</Typography>
                        </Box>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Descripción o busca artículo…"
                        sx={{ minWidth: 220 }} />
                    )}
                  />
                </TableCell>
                <TableCell align="right">
                  <TextField size="small" type="number" value={item.unitPrice}
                    onChange={e => updateItem(idx, 'unitPrice', parseFloat(e.target.value) || 0)}
                    inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                    sx={{ width: 110 }}
                  />
                </TableCell>
                <TableCell align="right">
                  <TextField size="small" type="number" value={item.discount}
                    onChange={e => updateItem(idx, 'discount', Math.min(100, parseFloat(e.target.value) || 0))}
                    inputProps={{ min: 0, max: 100, step: 1, style: { textAlign: 'right' } }}
                    sx={{ width: 70 }}
                  />
                </TableCell>
                <TableCell>
                  <Select size="small" value={item.taxRate}
                    onChange={e => updateItem(idx, 'taxRate', e.target.value)} sx={{ width: 80 }}>
                    {TAX_OPTIONS.map(t => <MenuItem key={t} value={t}>{t}%</MenuItem>)}
                  </Select>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600}>{fmt.currency(c.total)}</Typography>
                  {c.taxAmount > 0 && <Typography variant="caption" color="text.secondary" display="block">+{fmt.currency(c.taxAmount)} IVA</Typography>}
                </TableCell>
                <TableCell>
                  <Tooltip title="Eliminar línea">
                    <IconButton size="small" onClick={() => removeItem(idx)} color="error">
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 2 }}>
        <Button startIcon={<Add />} onClick={addItem} size="small" variant="outlined">
          Agregar artículo
        </Button>
        <Box sx={{ textAlign: 'right', minWidth: 220 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">Subtotal:</Typography>
            <Typography variant="body2" fontWeight={600}>{fmt.currency(totals.subtotal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">IVA:</Typography>
            <Typography variant="body2" fontWeight={600}>{fmt.currency(totals.tax)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '2px solid', borderColor: 'primary.main' }}>
            <Typography variant="body1" fontWeight={800}>Total:</Typography>
            <Typography variant="body1" fontWeight={800} color="primary.main">{fmt.currency(totals.total)}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
